import { useId } from "react";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_PAPER_SPEED,
  ECG_COLORS,
  MAJOR_MM,
  MINOR_MM,
  PX_PER_MM,
  ROW_HEIGHT_MM,
} from "./ecgChart.constants";
import {
  GridScrollArea,
  LeadLabelCell,
  LeadLabelColumn,
  ScrollableTrace,
} from "./EcgChart.styles";
import { PulseTrace } from "./EcgChartSkeleton.styles";

const LEAD_LABELS = ["I", "II", "III", "aVR", "aVL", "aVF"];

// One synthetic heartbeat as [dx (px), up (px above baseline)] control points,
// repeated across the row to look like an ECG rhythm while loading.
const BEAT_PX = 80; // ~0.8 s at 25 mm/s
const BEAT: [number, number][] = [
  [0, 0],
  [10, 0],
  [14, 5], // P wave
  [18, 0],
  [28, 0],
  [30, -4], // Q
  [33, 28], // R
  [36, -10], // S
  [40, 0],
  [52, 0],
  [60, 9], // T wave
  [70, 0],
  [80, 0],
];

function buildTrace(baseY: number, width: number): string {
  const points: string[] = [];

  for (let x0 = 0; x0 < width; x0 += BEAT_PX) {
    BEAT.forEach(([dx, up]) => {
      const x = x0 + dx;
      if (x <= width) points.push(`${x.toFixed(1)},${(baseY - up).toFixed(1)}`);
    });
  }

  return points.join(" ");
}

export interface IEcgChartSkeletonProps {
  leadCount?: number;
  durationSeconds?: number;
}

// Loading placeholder shaped like the real EcgChart: same pink ECG grid and
// lead label column, with a pulsing ghost trace per lead.
export function EcgChartSkeleton({
  leadCount = 6,
  durationSeconds = 30,
}: IEcgChartSkeletonProps) {
  const { t } = useTranslation();

  const rawId = useId().replace(/:/g, "");
  const minorId = `ecg-skel-minor-${rawId}`;
  const majorId = `ecg-skel-major-${rawId}`;

  const count = Math.min(Math.max(leadCount, 1), LEAD_LABELS.length);
  const leads = LEAD_LABELS.slice(0, count);

  const pxPerMm = PX_PER_MM;
  const pxPerSecond = DEFAULT_PAPER_SPEED * pxPerMm;
  const minorPx = MINOR_MM * pxPerMm;
  const majorPx = MAJOR_MM * pxPerMm;
  const rowHeightPx = ROW_HEIGHT_MM * pxPerMm;

  const totalHeightPx = rowHeightPx * count;
  const svgWidth = Math.max(durationSeconds, 1) * pxPerSecond;

  return (
    <GridScrollArea>
      <LeadLabelColumn>
        {leads.map((lead) => (
          <LeadLabelCell key={lead} style={{ height: rowHeightPx }}>
            {lead}
          </LeadLabelCell>
        ))}
      </LeadLabelColumn>

      <ScrollableTrace>
        <svg
          width={svgWidth}
          height={totalHeightPx}
          viewBox={`0 0 ${svgWidth} ${totalHeightPx}`}
          role="img"
          aria-label={t("patientPage_ecg_traceLoading")}
          shapeRendering="geometricPrecision"
        >
          <defs>
            <pattern
              id={minorId}
              width={minorPx}
              height={minorPx}
              patternUnits="userSpaceOnUse"
            >
              <path
                d={`M ${minorPx} 0 L 0 0 L 0 ${minorPx}`}
                fill="none"
                stroke={ECG_COLORS.minorLine}
                strokeWidth={0.6}
              />
            </pattern>
            <pattern
              id={majorId}
              width={majorPx}
              height={majorPx}
              patternUnits="userSpaceOnUse"
            >
              <path
                d={`M ${majorPx} 0 L 0 0 L 0 ${majorPx}`}
                fill="none"
                stroke={ECG_COLORS.majorLine}
                strokeWidth={1.1}
              />
            </pattern>
          </defs>

          <rect
            width={svgWidth}
            height={totalHeightPx}
            fill={ECG_COLORS.paper}
          />
          <rect
            width={svgWidth}
            height={totalHeightPx}
            fill={`url(#${minorId})`}
          />
          <rect
            width={svgWidth}
            height={totalHeightPx}
            fill={`url(#${majorId})`}
          />

          {leads.map((lead, index) => (
            <PulseTrace
              key={lead}
              points={buildTrace(
                index * rowHeightPx + rowHeightPx / 2,
                svgWidth
              )}
              fill="none"
              stroke={ECG_COLORS.majorLine}
              strokeWidth={1.4}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
        </svg>
      </ScrollableTrace>
    </GridScrollArea>
  );
}
